import React from 'react';
import * as moment from 'moment';
import { getMessage } from '@tcc/i18n';
import styles from './css/DailyForecast.css';
import { WeatherConditionsType } from '../types';
import { convertDirection } from '../utils/compass';
import { rmUnderscores } from '../utils/conditionFormatting';
import { createUnitOrNotApplicable } from '../utils/createUnitOrNotApplicable';
import { FeatureFlagKey } from '../../types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import SprayAssistBadge from './SprayAssist/SprayAssistBadge';
import SprayAssistInfo from './SprayAssist/SprayAssistInfo';

const images = require.context('../images', true);
const newImages = require.context('../images/new', true);

// moment format ddd was causing trobule when you toggle past weather and comeback
const getThreeLettersDay = (day) => day.format('dddd').slice(0, 3);

export function DailyForecast({
  dailyConditions: {
    firstForecastTime,
    maxTemperature,
    minTemperature,
    precipitationProbability,
    precipitationAmount,
    sprayAssistIndex,
    weatherCondition: { data: weatherConditionData },
    windDirection,
    windSpeed,
  },
}) {

  const days = [...Array(7)].map((day, index) =>
    moment.utc(firstForecastTime).add(index, 'days')
  );
  const weatherCondition = weatherConditionData;
  const isSprayAssistEnabled = useFeatureFlag(FeatureFlagKey.DMB_SPRAY_ASSIST);

  return (
    <div>
      <h2 className={styles.title}>{getMessage('forecast.daily.title')}</h2>
      <div
        className={styles.dayCellsContainer}
        data-test-id="day-cells-container"
      >
        {days.map((day, index) => {
          const condition = rmUnderscores(weatherCondition[index]);
          const maxTemp =
            maxTemperature.q[index] || maxTemperature.q[index] === 0
              ? createUnitOrNotApplicable(
                maxTemperature.q[index],
                maxTemperature.u
              )
                .forLocaleGroup('temperature')
                .toString()
              : '';
          const minTemp =
            minTemperature.q[index] || minTemperature.q[index] === 0
              ? createUnitOrNotApplicable(
                minTemperature.q[index],
                minTemperature.u
              )
                .forLocaleGroup('temperature')
                .toString()
              : '';
          const formattedWindSpeed = createUnitOrNotApplicable(
            windSpeed.q[index],
            windSpeed.u
          )
            .forLocaleGroup('wind-speed')
            .toString();

          const formattedPrecipitationAmount = precipitationAmount?.q?.[index] !== undefined
            ? createUnitOrNotApplicable(
              precipitationAmount.q[index],
              precipitationAmount.u
            )
              .forLocaleGroup('rainfall')
              .toString()
            : '';

          const formattedTemps = `${maxTemp} ${minTemp}`;
          const translateDirection = (angle) =>
            getMessage(`forecast.direction.${convertDirection(angle)}`);

          const weekDay = getMessage(`weekdays.${getThreeLettersDay(day)}`);
          const [month, dayNumber] = day.format('MMM D').split(' ');
          const translatedMonthAndDayNumer = `${getMessage(
            `monthNames.${month}`
          )} ${dayNumber}`;

          return (
            <div className={styles.dayCell} key={day}>

              {isSprayAssistEnabled ? (
                <div className={styles.weekDayMonthAndDayNumer}>{weekDay} {translatedMonthAndDayNumer}</div>
              ) : (
                <><div className={styles.weekDay}>{weekDay}</div>
                  <div>{translatedMonthAndDayNumer}</div></>
              )}

              <img
                alt={weatherCondition[index]}
                src={isSprayAssistEnabled
                  ? newImages(`./forecast-icons/small/${condition}.png`)
                  : images(`./forecast-icons/medium/${condition}.png`)
                }
              />

              <div>
                {getMessage(`forecast.conditions.${weatherCondition[index]}`)}
              </div>
              <div>{formattedTemps}</div>
              <div>
                {`${translateDirection(
                  windDirection.q[index]
                )} ${formattedWindSpeed}`}
              </div>
              <div>
                {getMessage('forecast.daily.precipPercent', {
                  precip: precipitationProbability.q[index],
                })}
                {isSprayAssistEnabled && formattedPrecipitationAmount && (
                  <span>
                    {`, ${formattedPrecipitationAmount}`}
                  </span>
                )}
              </div>
              {isSprayAssistEnabled && (
                <SprayAssistBadge data-test-id={`spray-assist-${sprayAssistIndex?.[index]}`} sprayAssistValue={sprayAssistIndex?.[index]} />
              )}
            </div>
          );
        })}
      </div>
      {isSprayAssistEnabled && (
        <SprayAssistInfo data-test-id="spray-info" />
      )}
    </div>
  );
}

DailyForecast.propTypes = {
  dailyConditions: WeatherConditionsType,
};
